import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Vault, MoveRight, Star, Download, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VaultDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [vault, setVault] = useState(null);
  const [images, setImages] = useState([]);
  const [collections, setCollections] = useState([]);
  const [vaults, setVaults] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedImage, setSelectedImage] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveDestination, setMoveDestination] = useState("main");
  const [moveCollectionId, setMoveCollectionId] = useState("none");
  const [moveVaultId, setMoveVaultId] = useState("none");
  const [moving, setMoving] = useState(false);

  const fetchAll = async () => {
    try {
      const [vaultRes, imagesRes, collectionsRes, vaultsRes] = await Promise.all([
        axios.get(`${API}/vaults`),
        axios.get(`${API}/bank`, { params: { vault_id: id } }),
        axios.get(`${API}/collections`),
        axios.get(`${API}/vaults`),
      ]);
      const found = vaultRes.data.find(v => v.id === id);
      setVault(found || null);
      setImages(imagesRes.data);
      setCollections(collectionsRes.data);
      setVaults(vaultsRes.data);
    } catch {
      toast.error("Failed to load vault");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  const refreshImages = async () => {
    const [imagesRes, vaultsRes] = await Promise.all([
      axios.get(`${API}/bank`, { params: { vault_id: id } }),
      axios.get(`${API}/vaults`),
    ]);
    setImages(imagesRes.data);
    setVaults(vaultsRes.data);
    if (selectedImage) {
      const updated = imagesRes.data.find(i => i.id === selectedImage.id);
      if (updated) setSelectedImage(updated);
    }
  };

  const openDetail = (img) => {
    setSelectedImage(img);
    setNewTitle(img.title || img.prompt || "");
    setEditingTitle(false);
    setComment("");
    setDetailOpen(true);
  };

  const updateTitle = async () => {
    if (!selectedImage || !newTitle.trim()) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { title: newTitle.trim() });
      toast.success("Title updated");
      setEditingTitle(false);
      refreshImages();
    } catch { toast.error("Failed to update title"); }
  };

  const updateRating = async (rating) => {
    if (!selectedImage) return;
    try {
      await axios.put(`${API}/images/${selectedImage.id}`, { rating });
      refreshImages();
    } catch { toast.error("Failed to update rating"); }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim() || !selectedImage) return;
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/images/${selectedImage.id}/comments`, { text: comment });
      setComment("");
      refreshImages();
    } catch { toast.error("Failed to add comment"); }
    finally { setSubmittingComment(false); }
  };

  const handleDownload = () => {
    if (!selectedImage) return;
    const link = document.createElement("a");
    link.href = selectedImage.image_data;
    link.download = `vault-image-${selectedImage.id}.png`;
    link.click();
  };

  const handleDelete = async (img) => {
    if (!window.confirm("Delete this image from Bank? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/images/${img.id}`);
      toast.success("Image deleted");
      setDetailOpen(false);
      fetchAll();
    } catch { toast.error("Failed to delete image"); }
  };

  const openMove = (img) => {
    setSelectedImage(img);
    setMoveDestination("main");
    setMoveCollectionId("none");
    setMoveVaultId("none");
    setMoveOpen(true);
  };

  const handleMove = async () => {
    if (!selectedImage) return;
    setMoving(true);
    try {
      await axios.post(`${API}/images/${selectedImage.id}/move`, {
        destination: moveDestination,
        collection_id: moveDestination === "main" && moveCollectionId !== "none" ? moveCollectionId : null,
        vault_id: moveDestination === "bank" && moveVaultId !== "none" ? moveVaultId : null,
      });
      toast.success(moveDestination === "main" ? "Copied to Winners — original stays in Bank" : "Moved to Bank");
      setMoveOpen(false);
      setDetailOpen(false);
      fetchAll();
    } catch { toast.error("Failed to move image"); }
    finally { setMoving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full spinner" />
    </div>
  );

  if (!vault) return (
    <div className="text-center py-16">
      <p className="text-gray-500">Vault not found.</p>
      <Link to="/bank" className="text-sm text-black underline mt-2 inline-block">Back to Bank</Link>
    </div>
  );

  return (
    <div className="pb-20 md:pb-0">
      {/* Header */}
      <div className="mb-6">
        <Link to="/bank" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-4">
          <ArrowLeft size={16} /> Back to Bank
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black text-white flex items-center justify-center flex-shrink-0">
            <Vault size={18} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{vault.name}</h1>
            {vault.description && <p className="text-gray-500 text-sm mt-0.5">{vault.description}</p>}
          </div>
        </div>
      </div>

      {/* Images */}
      {images.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-8 text-center">
          <Vault className="mx-auto text-gray-400 mb-3" size={32} />
          <p className="text-gray-500 text-sm">This vault is empty</p>
          <Link to="/bank" className="text-sm text-black underline mt-2 inline-block">Upload via Bank</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {images.map((img) => (
            <div key={img.id} className="border border-gray-200 hover:border-black transition-colors">
              <div className="aspect-square bg-gray-100 cursor-pointer" onClick={() => openDetail(img)}>
                <img src={img.image_data} alt={img.title || img.prompt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-2 border-t border-gray-200">
                <p className="text-xs truncate text-gray-700 mb-1">{img.title || img.prompt}</p>
                <div className="flex justify-end">
                  <button onClick={() => openMove(img)} className="text-gray-400 hover:text-black transition-colors" title="Move image">
                    <MoveRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Vault Image</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              <img src={selectedImage.image_data} alt={selectedImage.title} className="w-full border border-gray-200" />
              <div className="flex gap-2">
                <Button onClick={handleDownload} variant="outline" size="sm" className="rounded-none flex-1">
                  <Download size={14} className="mr-2" /> Download
                </Button>
                <Button onClick={() => { setDetailOpen(false); openMove(selectedImage); }} size="sm" className="rounded-none flex-1 bg-black text-white hover:bg-gray-800">
                  <MoveRight size={14} className="mr-2" /> Copy to Winners
                </Button>
                {(user?.role === "admin" || user?.can_delete_bank) && (
                  <Button onClick={() => handleDelete(selectedImage)} variant="outline" size="sm" className="rounded-none text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
              <div className="space-y-3 pt-2 border-t border-gray-100">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs uppercase tracking-widest text-gray-500">Title</p>
                    {!editingTitle && <button onClick={() => setEditingTitle(true)} className="text-xs text-gray-500 hover:text-black underline">Edit</button>}
                  </div>
                  {editingTitle ? (
                    <div className="flex gap-2">
                      <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="flex-1 px-2 py-1 text-sm border border-gray-300 bg-white text-black" autoFocus />
                      <Button onClick={updateTitle} size="sm" className="rounded-none bg-black text-white px-3">Save</Button>
                      <Button onClick={() => { setEditingTitle(false); setNewTitle(selectedImage.title || selectedImage.prompt || ""); }} size="sm" variant="outline" className="rounded-none px-3">Cancel</Button>
                    </div>
                  ) : (
                    <p className="text-sm">{selectedImage.title || selectedImage.prompt || "-"}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Uploaded</p>
                  <p className="text-sm text-gray-700">
                    {selectedImage.created_at
                      ? new Date(selectedImage.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Rating</p>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map((star) => (
                      <button key={star} onClick={() => updateRating(star)} className="p-0.5">
                        <Star size={18} className={star <= (selectedImage.rating || 0) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                    Comments {selectedImage.comments?.length > 0 && `(${selectedImage.comments.length})`}
                  </p>
                  {selectedImage.comments?.length > 0 && (
                    <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
                      {selectedImage.comments.map((c, i) => (
                        <div key={i} className="p-2 bg-gray-50 border border-gray-200 text-sm">
                          <p className="font-medium text-xs text-black">{c.user_name}</p>
                          <p className="text-gray-600">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <form onSubmit={addComment} className="flex gap-2">
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." className="rounded-none border-gray-300 bg-white text-black resize-none text-sm" rows={2} />
                    <Button type="submit" disabled={submittingComment || !comment.trim()} className="rounded-none bg-black text-white hover:bg-gray-800 self-end">
                      {submittingComment ? "..." : "Add"}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="rounded-none bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-black">Move Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest text-gray-500">Destination</label>
              <Select value={moveDestination} onValueChange={setMoveDestination}>
                <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Copy to Winners (stays in Bank)</SelectItem>
                  <SelectItem value="bank">Move to different Vault</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {moveDestination === "main" && (
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-gray-500">Collection (optional)</label>
                <Select value={moveCollectionId} onValueChange={setMoveCollectionId}>
                  <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                    <SelectValue placeholder="No collection" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No collection</SelectItem>
                    {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {moveDestination === "bank" && (
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-widest text-gray-500">Vault (optional)</label>
                <Select value={moveVaultId} onValueChange={setMoveVaultId}>
                  <SelectTrigger className="rounded-none border-gray-300 bg-white text-black">
                    <SelectValue placeholder="No vault" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No vault</SelectItem>
                    {vaults.filter(v => v.id !== id).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={handleMove} disabled={moving} className="w-full rounded-none bg-black text-white hover:bg-gray-800">
              <MoveRight size={16} className="mr-2" />
              {moving ? "Moving..." : "Move Image"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
